import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { createTranslator } from '@/i18n';
import { Eye, Send, Loader2, FileDown, Copy, CheckCircle2, Clock, PenLine, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Field = ({ label, value, onChange, testId, ...props }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...props} />
  </div>
);

const Sel = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid={testId}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export const ContractDialog = ({ employee, open, onClose }) => {
  const { language: uiLang } = useLanguage();
  const t = createTranslator(uiLang || 'is');
  const [data, setData] = useState(null);
  const [language, setLanguage] = useState('is');
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [sendResult, setSendResult] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signConfirmed, setSignConfirmed] = useState(false);

  useEffect(() => {
    if (!open || !employee) return;
    setSendResult(null);
    setSignOpen(false);
    setSignConfirmed(false);
    const load = async () => {
      try {
        const [pre, list] = await Promise.all([
          axios.get(`${API}/contracts/prefill/${employee.id}`, authHeaders()),
          axios.get(`${API}/contracts/by-employee/${employee.id}`, authHeaders()),
        ]);
        setData(pre.data);
        setRecipient(pre.data.employee_email || '');
        setSignerName(pre.data.signature_name || '');
        setContracts(list.data);
      } catch {
        toast.error('Villa við að sækja samningsgögn');
      }
    };
    load();
  }, [open, employee]);

  const set = (key) => (val) => setData((d) => ({ ...d, [key]: val }));

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/contracts/preview`, { language, data },
        { ...authHeaders(), responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch {
      toast.error('Villa við forskoðun');
    } finally {
      setPreviewing(false);
    }
  };

  const openSignDialog = () => {
    if (!recipient) { toast.error('Vantar netfang starfsmanns'); return; }
    setSignConfirmed(false);
    setSignOpen(true);
  };

  const nameDiffers = () => {
    const profile = (data?.admin_full_name || '').trim().toLowerCase();
    const typed = signerName.trim().toLowerCase();
    return profile && typed && profile !== typed;
  };

  const handleSignAndSend = async () => {
    if (!signConfirmed || !signerName.trim()) return;
    setSending(true);
    try {
      // 1) Vélrituð undirskrift atvinnurekanda - bundin document hash/version
      const signRes = await axios.post(`${API}/contracts/employer-sign`, {
        employee_id: employee.id, language, data,
        signer_full_name: signerName.trim(), confirm: true,
      }, authHeaders());
      // 2) Sending á starfsmann í rafræna undirritun
      const res = await axios.post(`${API}/contracts/send`, {
        contract_id: signRes.data.contract_id, data, language,
        recipient_email: recipient, base_url: window.location.origin,
      }, { ...authHeaders(), timeout: 60000 });
      setSendResult(res.data);
      setSignOpen(false);
      if (res.data.email_sent) {
        toast.success(`Samningur undirritaður og sendur á ${recipient}!`);
      } else {
        toast.warning('Samningur undirritaður en tölvupóstur fór ekki – afritaðu hlekkinn hér að neðan');
      }
      const list = await axios.get(`${API}/contracts/by-employee/${employee.id}`, authHeaders());
      setContracts(list.data);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(t('contract_signature.document_changed'));
      } else {
        const msg = err.response?.data?.detail
          || (err.request && !err.response ? 'Netvilla – ekki náðist samband við þjóninn. Reyndu aftur.' : err.message)
          || 'Villa við sendingu';
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = async (c) => {
    try {
      const res = await axios.get(`${API}/contracts/${c.id}/pdf`, { ...authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `radningarsamningur_${(data?.employee_name || 'starfsmadur').replace(/ /g, '_')}.pdf`;
      a.click();
    } catch {
      toast.error('Villa við niðurhal');
    }
  };

  const copyLink = (c) => {
    navigator.clipboard.writeText(`${window.location.origin}/sign/${c.token}`);
    toast.success('Hlekkur afritaður!');
  };

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="sr-only">Ráðningarsamningur</DialogTitle>
          </DialogHeader>
          <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="contract-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Ráðningarsamningur – {data.employee_name}</DialogTitle>
        </DialogHeader>

        {contracts.length > 0 && (
          <div className="border rounded-md divide-y mb-2">
            {contracts.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`contract-row-${c.id}`}>
                <span className="flex items-center gap-2">
                  {c.status === 'signed'
                    ? <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="h-4 w-4" /> Undirritaður</span>
                    : c.status === 'signature_invalidated'
                      ? <span className="flex items-center gap-1 text-red-600"><AlertTriangle className="h-4 w-4" /> {t('contract_signature.signature_invalidated')}</span>
                      : c.status === 'employer_signed'
                        ? <span className="flex items-center gap-1 text-blue-600"><PenLine className="h-4 w-4" /> {t('contract_signature.signed_by_employer')}</span>
                        : <span className="flex items-center gap-1 text-amber-600"><Clock className="h-4 w-4" /> Bíður undirritunar</span>}
                  <span className="text-gray-500">{new Date(c.created_at).toLocaleDateString('is-IS')} ({c.language.toUpperCase()})</span>
                  {c.employer_signature?.signer_full_name && (
                    <span className="text-xs text-gray-400">✍ {c.employer_signature.signer_full_name}</span>
                  )}
                </span>
                <span className="flex gap-1">
                  {c.status !== 'signed' && (
                    <Button variant="ghost" size="sm" onClick={() => copyLink(c)} data-testid={`copy-link-${c.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => downloadPdf(c)} data-testid={`download-pdf-${c.id}`}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Tungumál samnings" value={language} onChange={setLanguage} testId="contract-language-select"
              options={[['is', 'Íslenska'], ['en', 'English']]} />
            <Field label="Netfang starfsmanns (móttakandi)" value={recipient} onChange={setRecipient} testId="contract-recipient-input" />
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">1. Starf og ábyrgð</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starfsheiti" value={data.position} onChange={set('position')} testId="c-position" />
              <Field label="Næsti yfirmaður" value={data.supervisor} onChange={set('supervisor')} testId="c-supervisor" />
              <div className="col-span-2">
                <Field label="Starfslýsing (valfrjálst)" value={data.job_description} onChange={set('job_description')} testId="c-jobdesc" />
              </div>
              <div className="col-span-2">
                <Field label="Vinnustaður" value={data.workplace} onChange={set('workplace')} testId="c-workplace" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">2. Ráðningartími</h3>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Fyrsti starfsdagur" value={data.first_day} onChange={set('first_day')} testId="c-firstday" />
              <Sel label="Ráðning" value={data.employment_type} onChange={set('employment_type')} testId="c-emptype"
                options={[['permanent', 'Ótímabundin'], ['fixed', 'Tímabundin']]} />
              {data.employment_type === 'fixed' && (
                <div className="grid grid-cols-2 gap-2 col-span-1">
                  <Field label="Frá" value={data.fixed_from} onChange={set('fixed_from')} type="date" testId="c-fixedfrom" />
                  <Field label="Til" value={data.fixed_to} onChange={set('fixed_to')} type="date" testId="c-fixedto" />
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">3. Vinnutími</h3>
            <div className="grid grid-cols-3 gap-3">
              <Sel label="Starfshlutfall" value={data.job_percentage} onChange={set('job_percentage')} testId="c-jobpct"
                options={[['full', 'Fullt starf'], ['part', 'Hlutastarf']]} />
              {data.job_percentage === 'part' && (
                <Field label="Hlutfall %" value={data.part_percent} onChange={set('part_percent')} testId="c-partpct" />
              )}
              <Field label="Virkur vinnutími klst/viku" value={data.weekly_hours} onChange={set('weekly_hours')} testId="c-weeklyhours" />
              <Field label="Vinnutími frá (t.d. 08:00)" value={data.work_hours_from} onChange={set('work_hours_from')} testId="c-hoursfrom" />
              <Field label="Vinnutími til (t.d. 17:00)" value={data.work_hours_to} onChange={set('work_hours_to')} testId="c-hoursto" />
              <Field label="Hádegismatur mín/dag" value={data.lunch_minutes} onChange={set('lunch_minutes')} testId="c-lunch" />
              <Field label="Kaffihlé mín/dag (valfrjálst)" value={data.coffee_minutes} onChange={set('coffee_minutes')} testId="c-coffee" />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">4. Launakjör</h3>
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Launaform" value={data.pay_option} onChange={set('pay_option')} testId="c-payoption"
                options={[['A', 'Mánaðarlaun'], ['B', 'Föst heildarlaun (m. yfirvinnu innifalinni)'], ['C', 'Tímakaup']]} />
              {data.pay_option === 'A' && (
                <>
                  <Field label="Mánaðarlaun kr." value={data.monthly_wage} onChange={set('monthly_wage')} testId="c-monthlywage" />
                  <Field label="Dagvinnutímakaup kr./klst (reiknað)" value={data.hourly_day} onChange={set('hourly_day')} testId="c-hourlyday" />
                  <div className="col-span-2">
                    <Field label="Yfirvinnukaup (lýsing)" value={data.overtime_desc} onChange={set('overtime_desc')} testId="c-otdesc" />
                  </div>
                </>
              )}
              {data.pay_option === 'B' && (
                <Field label="Föst mánaðarlaun kr." value={data.fixed_monthly_wage} onChange={set('fixed_monthly_wage')} testId="c-fixedwage" />
              )}
              {data.pay_option === 'C' && (
                <>
                  <Field label="Dagvinnutímakaup kr./klst" value={data.hourly_day} onChange={set('hourly_day')} testId="c-hourlyday" />
                  <Field label="Yfirvinna kr./klst" value={data.hourly_overtime} onChange={set('hourly_overtime')} testId="c-hourlyot" />
                  <div className="col-span-2">
                    <Field label="Mánaðarlaun kr. (reiknuð mánaðarsamsvörun m.v. deilitölu)" value={data.monthly_wage} onChange={set('monthly_wage')} testId="c-monthlywage" />
                    <p className="text-xs text-gray-500 mt-1">Mánaðarlaun birtast alltaf á samningnum – hjá tímakaupsfólki sem reiknuð mánaðarsamsvörun tímakaups.</p>
                  </div>
                </>
              )}
              <Sel label="Desember- og orlofsuppbót" value={data.bonus_option} onChange={set('bonus_option')} testId="c-bonus"
                options={[['kjarasamningur', 'Skv. kjarasamningi'], ['included', 'Innifalið í launum']]} />
              <Field label="Ökutækjastyrkur kr./mán (valfrjálst)" value={data.car_allowance} onChange={set('car_allowance')} testId="c-car" />
              <Field label="Bankareikningur (valfrjálst)" value={data.bank_account} onChange={set('bank_account')} testId="c-bank" />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">5–6. Orlof og veikindi</h3>
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Orlof" value={data.vacation_option} onChange={set('vacation_option')} testId="c-vacopt"
                options={[['kjarasamningur', 'Skv. kjarasamningi'], ['agreed', 'Umsamið']]} />
              {data.vacation_option === 'agreed' && (
                <Field label="Orlofsdagar" value={data.vacation_days} onChange={set('vacation_days')} testId="c-vacdays" />
              )}
              <Sel label="Veikindaréttur" value={data.sick_option} onChange={set('sick_option')} testId="c-sickopt"
                options={[['kjarasamningur', 'Skv. kjarasamningi'], ['agreed', 'Umsamið']]} />
              {data.sick_option === 'agreed' && (
                <Field label="Mánuðir á 12 mán. tímabili" value={data.sick_months} onChange={set('sick_months')} testId="c-sickmonths" />
              )}
              <div className="col-span-2">
                <Field label="Tilkynning veikinda" value={data.sick_notification} onChange={set('sick_notification')} testId="c-sicknotif" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">7. Stéttarfélag og lífeyrissjóður</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Stéttarfélag" value={data.union_name} onChange={set('union_name')} testId="c-union" />
              <Field label="Lífeyrissjóður" value={data.pension_fund} onChange={set('pension_fund')} testId="c-pension" />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2 border-b pb-1">Gildistaka og undirritun</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Samningur gildir frá (dd.mm.áááá)" value={data.effective_date} onChange={set('effective_date')} testId="c-effective" />
              <Field label="Staður og dagsetning" value={data.place_date} onChange={set('place_date')} testId="c-placedate" />
            </div>
            <p className="text-xs text-gray-500 bg-gray-50 border rounded-md px-3 py-2" data-testid="typed-sig-hint">
              {t('contract_signature.typed_hint')}
            </p>
          </div>

          {sendResult?.email_sent && (
            <div className="bg-green-50 border border-green-300 rounded-md p-3 text-sm text-green-800" data-testid="send-success-box">
              ✓ Samningur sendur á {sendResult.sent_to || recipient}. Starfsmaðurinn fær tölvupóst með undirritunarhlekk.
            </div>
          )}

          {sendResult && !sendResult.email_sent && (
            <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm" data-testid="send-link-fallback">
              <p className="font-medium text-amber-800">Tölvupóstur fór ekki ({sendResult.email_error || 'póstur ekki uppsettur'}).</p>
              <p className="text-amber-700 mt-1 break-all">Sendu starfsmanninum hlekkinn handvirkt: {sendResult.sign_url}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => { navigator.clipboard.writeText(sendResult.sign_url); toast.success('Afritað!'); }} data-testid="copy-fallback-link">
                <Copy className="mr-1 h-4 w-4" /> Afrita hlekk
              </Button>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="contract-preview-btn">
              {previewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
              Forskoða PDF
            </Button>
            <Button onClick={openSignDialog} disabled={sending} className="flex-1" data-testid="contract-send-btn">
              <PenLine className="mr-2 h-4 w-4" />
              {t('contract_signature.sign_and_send')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Undirritunargluggi atvinnurekanda - vélrituð undirskrift */}
    <Dialog open={signOpen} onOpenChange={(o) => !o && setSignOpen(false)}>
      <DialogContent className="max-w-lg" data-testid="employer-sign-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('contract_signature.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">{t('contract_signature.full_name')}</Label>
            <Input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="t.d. Árni Þór Jónsson"
              data-testid="signer-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('contract_signature.preview')}</Label>
            <div
              className="border rounded-md bg-gray-50 px-4 py-5 min-h-[90px] flex items-center justify-center"
              data-testid="signature-preview"
            >
              <span style={{ fontFamily: "'Great Vibes', cursive", fontSize: '2.4rem', color: '#1a2a5a', lineHeight: 1.2 }}>
                {signerName.trim() || '—'}
              </span>
            </div>
          </div>
          {nameDiffers() && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="name-differs-warning">
              ⚠ {t('contract_signature.name_differs_warning')}
            </p>
          )}
          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="sign-confirm-label">
            <input
              type="checkbox"
              checked={signConfirmed}
              onChange={(e) => setSignConfirmed(e.target.checked)}
              className="mt-1"
              data-testid="sign-confirm-checkbox"
            />
            <span>{t('contract_signature.confirm')}</span>
          </label>
          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setSignOpen(false)} disabled={sending} data-testid="sign-cancel-btn">
              {t('contract_signature.cancel')}
            </Button>
            <Button
              onClick={handleSignAndSend}
              disabled={!signConfirmed || !signerName.trim() || sending}
              className="flex-1"
              data-testid="sign-and-send-btn"
            >
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {sending ? t('contract_signature.signing') : t('contract_signature.sign_and_send')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
